/**
 * Civil business dates as 'YYYY-MM-DD' strings. No Date on any boundary (ADR-004).
 * Calendar math is pure Y/M/D arithmetic so DST cannot shift a purchase day.
 */

export type BusinessDate = string & { readonly __brand: 'BusinessDate' };

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

type Ymd = { readonly year: number; readonly month: number; readonly day: number };

function isLeapYear(year: number): boolean {
  return year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
}

export function daysInMonth(year: number, month: number): number {
  if (month < 1 || month > 12) throw new Error('month must be 1–12');
  switch (month) {
    case 2:
      return isLeapYear(year) ? 29 : 28;
    case 4:
    case 6:
    case 9:
    case 11:
      return 30;
    default:
      return 31;
  }
}

function parseYmd(s: string): Ymd {
  const m = DATE_RE.exec(s);
  if (!m) throw new Error('BusinessDate must be YYYY-MM-DD');
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (month < 1 || month > 12) throw new Error('invalid month');
  const dim = daysInMonth(year, month);
  if (day < 1 || day > dim) throw new Error('invalid day for month');
  return { year, month, day };
}

function formatYmd(year: number, month: number, day: number): BusinessDate {
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${String(year).padStart(4, '0')}-${mm}-${dd}` as BusinessDate;
}

/** Validate and brand a civil date string. */
export function businessDate(s: string): BusinessDate {
  const { year, month, day } = parseYmd(s);
  return formatYmd(year, month, day);
}

/** Clamp day-of-month into [1, daysInMonth] (statement_day_of_month anchors). */
export function clampDayOfMonth(year: number, month: number, dayOfMonth: number): number {
  if (dayOfMonth < 1 || dayOfMonth > 31) throw new Error('dayOfMonth must be 1–31');
  const dim = daysInMonth(year, month);
  return dayOfMonth > dim ? dim : dayOfMonth;
}

/** Date in (year, month) on statement_day_of_month, month-length-clamped. */
export function onDayOfMonth(year: number, month: number, dayOfMonth: number): BusinessDate {
  return formatYmd(year, month, clampDayOfMonth(year, month, dayOfMonth));
}

export function compareBusinessDates(a: BusinessDate, b: BusinessDate): -1 | 0 | 1 {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

/** cycle_start <= date <= cycle_end */
export function isInInclusiveRange(
  date: BusinessDate,
  start: BusinessDate,
  end: BusinessDate,
): boolean {
  return compareBusinessDates(date, start) >= 0 && compareBusinessDates(date, end) <= 0;
}

export function addDays(date: BusinessDate, delta: number): BusinessDate {
  assertInteger(delta, 'delta days');
  let { year, month, day } = parseYmd(date);
  day += delta;
  while (day > daysInMonth(year, month)) {
    day -= daysInMonth(year, month);
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  while (day < 1) {
    month -= 1;
    if (month < 1) {
      month = 12;
      year -= 1;
    }
    day += daysInMonth(year, month);
  }
  return formatYmd(year, month, day);
}

/**
 * Add calendar months, preserving the original day-of-month when possible and
 * clamping when the target month is shorter (31 → Feb/Apr/…).
 */
export function addMonthsClamped(date: BusinessDate, deltaMonths: number): BusinessDate {
  assertInteger(deltaMonths, 'delta months');
  const { year, month, day } = parseYmd(date);
  const absolute = year * 12 + (month - 1) + deltaMonths;
  const y = Math.floor(absolute / 12);
  const m = (absolute % 12) + 1;
  return onDayOfMonth(y, m, day);
}

/** Signed civil-day difference: end − start. */
export function diffDays(start: BusinessDate, end: BusinessDate): number {
  const a = parseYmd(start);
  const b = parseYmd(end);
  return civilSerial(b) - civilSerial(a);
}

/** Days from a fixed epoch — Gregorian, timezone-free. */
function civilSerial({ year, month, day }: Ymd): number {
  // Algorithm from civil calendar → day count (Howard Hinnant / std::chrono).
  const y = month <= 2 ? year - 1 : year;
  const era = Math.floor(y / 400);
  const yoe = y - era * 400;
  const m = month <= 2 ? month + 9 : month - 3;
  const doy = Math.floor((153 * m + 2) / 5) + day - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return era * 146097 + doe - 719468;
}

function assertInteger(n: number, label: string): void {
  if (!Number.isInteger(n)) throw new Error(`${label} must be an integer`);
}
